(ns frontend.view.nev.real-estate.view
  (:require
   [frontend.actions.common :as common-actions]
   [reagent.core :as r]
   [common.localization :as loc]
   [clojure.string :as string]
   [frontend.logging :as logging]
   [frontend.view.entity-viewers.header :as header]
   [frontend.view.field-templates :as templates]
   [frontend.view.nev.events :as events]
   [common.entity :as entity]
   [frontend.view.entity-viewers.entity-editor :as entity-editor]
   [frontend.view.not-found :as not-found]
   [frontend.routing :as routing]
   [frontend.view.entity-viewers.event-pane :as event-pane]
   [frontend.view.entity-viewers.modules :as modules]
   [frontend.state.current-session :as session]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.view.entity-viewers.utils :as entity-utils]
   [frontend.view.syncing-data :as syncing]
   [frontend.db :as app-db]
   [frontend.view.entity-viewers.components :as nev-components]
   [common.intelligence.common :as ic]
   [frontend.view.entity-viewers.modules.associated-entities :as associated-entities]
   [frontend.utils :as fu]
   [common.schema.field :as field]
   [frontend.state.permissions :as f-permissions]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [common.schema.registry :as registry]
   [ui.components.button.view :as button]))

(defn toolbar-buttons
  [{:keys [entity] :as _view-args}]
  (let [entity-id (:db/id entity)
        flagged? (contains? (:account/flagged-entities @session/account)
                            entity-id)]
    (concat
     [(zap-toolbar/dropdown-button
       {:icon      :fa-star
        :on-select (partial common-actions/toggle-flag entity flagged?)
        :ord       1
        :label     (loc/tr (if flagged? :unflag-entity-menu-item :flag-entity-menu-item))})]

     [(when-not (:common/disabled? entity)
        (zap-toolbar/delete-button entity [:real-estates-grid]))]

     [(nev-components/audit-trail-button {:entity     entity
                                          :entity-ids [entity-id]})])))

(defn header-top
  [{:keys [entity read-only?]}]
  [:div
   {:class "flex gap-2"}
   [:span.entity-logo-container.icon-container
    {:style {:padding-left "0"}}
    [:i
     {:class-name (fu/cs :fal (entity/default-icon entity))}]]
   [:div.nev-name-row-container
    [nev-components/name-view
     {:entity     entity
      :read-only? read-only?}]]])

(def default-field-defs
  [{:fields       {0 {:field :asset/description}}
    :field-layout :f-4}])

(defn header-board
  [{:keys [_entity-atom]}]
  (let [header-state (r/atom {:edit-layout? false})
        set-edit-layout! (fn [val] (swap! header-state assoc :edit-layout? val))]
    (fn [{:keys [entity-atom]}]
      (let [entity @entity-atom
            entity-id (:db/id entity)
            field-defs-path [:real-estate :header :field-defs]
            field-defs-timeout-id :real-estate-field-defs
            field-defs (or (events/timeout-value field-defs-timeout-id
                                                 @(r/track session/get-view-config field-defs-path))
                           default-field-defs)
            overrides nil
            selected-fields (->> field-defs
                                 (map :fields)
                                 (mapcat vals)
                                 (keep :field))
            remove-fields #{}
            schema (templates/entity-type-template
                    {:entity-type     :entity.type/real-estate
                     :selected-fields selected-fields
                     :tenant-schema   @session/tenant-schema
                     :account         @session/account
                     :role            @session/role
                     :remove-fields   remove-fields
                     :overrides       overrides})

            ;;test all fields:
            #_#_field-defs
              (->> schema
                   (map first)
                   (partition-all 4 4)
                   (map (fn [fields]
                          (->> fields
                               (map-indexed (fn [i f]
                                              [i {:field f}])))))
                   (map (partial into {}))
                   (map (fn [fields]
                          {:fields       fields
                           :field-layout :f-1-1-1-1}))
                   vec)]
        [:div
         [header/header-board
          {:top              [header-top {:entity entity}]
           :preview-top      [header-top
                              {:entity     entity
                               :read-only? true}]
           :actions          (toolbar-buttons {:entity entity})
           :entity-id        entity-id
           :entity-atom      entity-atom
           :edit-layout?     (:edit-layout? @header-state)
           :set-edit-layout! set-edit-layout!
           :field-defs       field-defs
           :schema           schema
           :save-layout!     (fn [new-field-defs]
                               (events/save-field-defs! field-defs-timeout-id
                                                        field-defs-path
                                                        new-field-defs
                                                        (fn [])
                                                        (fn [err]
                                                          (logging/error
                                                           "Failed to save field layout"
                                                           err))))}]]))))

(defn new-header?
  []
  (and (session/has-feature-access? :feature/customizable-nev)
       @session/customizable-nev?))

(defn real-estate-view
  [{:keys [real-estate-id _is-guest?] :as _view-args}]
  (let [state-atom (r/cursor app-db/app-state [:views :nev :real-estate])
        initialized? (r/atom false)
        temp-atom (r/cursor state-atom [:temp])
        view-state-atom (r/cursor state-atom [:view-state])
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        events-pane-atom (r/cursor state-atom [:events-pane])
        entity-atom (r/cursor session/entities [real-estate-id])]
    (r/create-class
     {:component-did-update
      (partial entity-utils/component-did-update
               :real-estate-id
               (partial events/clear-errors! error-atom notification-atom))
      :component-did-mount
      (fn [_]
        (entity-utils/reset-state! state-atom real-estate-id
                                   {:always-fetch? (session/new-grid-2023?)})
        (reset! initialized? true))
      :component-will-unmount
      (fn [_]
        (events/clear-errors! error-atom notification-atom)
        (entity-utils/clear-state! state-atom))
      :reagent-render
      (fn [{:keys [real-estate-id] :as view-args}]
        (let [real-estate (get @session/entities real-estate-id)
              enriched-entity (ic/enrich-entity real-estate)]
          (cond
            (not @initialized?)
            [syncing/view]
            (or (:common/disabled? real-estate)
                (:not-found @state-atom))
            [not-found/not-found-content]
            (:loading @state-atom)
            [syncing/view]
            :else
            (if (or (not real-estate)
                    (:common/disabled? real-estate))
              [:div " "]
              (let [tenant-schema @session/tenant-schema
                    entities-where-target-asset
                    (concat (when (session/dealflow?)
                              (->> @session/all-oppos
                                   (filter (fn [o]
                                             ;; TODO: @pirinen - filter based on access to pipeline where deal is ?
                                             (when-not (:common/disabled? o)
                                               (= (:oppo/target-asset o) real-estate-id))))))
                            (when (session/portfolio?)
                              (->> @session/all-holdings
                                   (filter (fn [h]
                                             ;; TODO: @pirinen - filter based on something else ???
                                             (when-not (:common/disabled? h)
                                               (= (:holding/target-asset h) real-estate-id)))))))
                    deals-where-re-target (->> @session/all-oppos
                                               (filter
                                                (fn [o]
                                                  (when-not (:common/disabled? o)
                                                    (some #{real-estate-id}
                                                          (:oppo/candidate-target-real-estates
                                                           o)))))
                                               (doall))
                    selected-deal-columns [:oppo/name :oppo/status]
                    header-fields [:asset/description]
                    view-id :real-estate
                    module-collapsed-path (fn [module-id]
                                            [view-id :modules :module-config module-id
                                             :collapsed?])]
                [:<>
                 (when-not (session/is-guest?)
                   [zap-toolbar/nev-toolbar
                    {:state           state-atom
                     :label           (partial zap-toolbar/toolbar-back
                                               [:real-estates-grid]
                                               (loc/tr :back-to/real-estates-grid))
                     :actions         (toolbar-buttons (assoc view-args
                                                              :entity
                                                              real-estate))
                     :dropdown-toggle [:span (loc/tr :actions)
                                       [:i.fas.fa-angle-down.ml-2]]
                     :dropdown-toggle-class-name
                     "zap-button zap-button--primary-btn actions-dropdown"}])
                 [:div.entity__container
                  (when @error-atom
                    [nev-components/error-bar error-atom])
                  (when @notification-atom
                    [nev-components/notification-bar notification-atom])

                  [:div.entity
                   [:div
                    {:class "w-full max-w-[1920px] mx-auto p-4"}
                    (if (new-header?)
                      [header-board {:entity-atom entity-atom}]
                      [nev-components/header-module
                       {:component-state temp-atom
                        :view-state      view-state-atom
                        :entity          real-estate
                        :enriched-entity enriched-entity
                        :entity-atom     entity-atom
                        :selected-fields header-fields}])
                    [:div.md:grid.grid-cols-10.gap-4
                     [:div
                      {:class (if (session/event-pane-disabled?)
                                "col-span-10"
                                "col-span-10 lg:col-span-5")}
                      [:div.entity-left
                       [modules/view
                        {:view-id  view-id
                         :ordering (get-in @session/view-config [view-id :modules :ordering])
                         :save-modules-order! (partial events/save-modules-order!
                                                       (:entity/type real-estate))
                         :modules
                         (concat
                          [(merge
                            {:module-id  :real-estate-information
                             :header     (loc/tr :nev-module/about-this-entity-type
                                                 [(string/lower-case (loc/tr (:entity/type real-estate)))])
                             ;;TODO uncomment when finished:
                             #_:header-tools
                             #_(when (session/is-admin?)
                                 [:span.entity-editor-header-settings
                                  {:on-click (fn [e]
                                                 ;;open settings
                                               )}
                                  [:span (loc/tr :entity-editor/settings-label)]
                                  [:i.fal.fa-cog]])
                             :collapsed? (get-in @session/view-config
                                                 (module-collapsed-path :real-estate-information))
                             :save-module-collapsed! (partial events/save-module-collapsed!
                                                              :entity.type/real-estate)
                             :content    [:div
                                          [entity-editor/view
                                           {:state-atom    state-atom
                                            :entity-id     real-estate-id
                                            :view-id       view-id
                                            :module-id     :real-estate-information
                                            :remove-fields (concat [:asset/name
                                                                    :common/network-drive :common/named-network-drives]
                                                                   (field/entity-tag-fields
                                                                    tenant-schema
                                                                    (:entity/type real-estate))
                                                                   header-fields)
                                            :ordering      (get-in
                                                            @session/view-config
                                                            [view-id :modules :module-config
                                                             :real-estate-information
                                                             :ordering])}]]}
                            (when-let [_city (some->> real-estate
                                                      :asset/city)]
                              {:header-tools
                               [:span.grow.flex.justify-end
                                [button/secondary-open
                                 {:tooltip (loc/tr :open-address-in-google-maps)}
                                 [:i.fal.fa-map-marker-alt
                                  {:on-click (fn [_]
                                               (let [asset (get @session/entities real-estate-id)]
                                                 (.open js/window
                                                        (str "https://www.google.com/maps/place/"
                                                             (:asset/street-address asset)
                                                             ","
                                                             (when-let [zip (:asset/zip-code asset)]
                                                               (str zip "+"))
                                                             (:asset/city asset)))))}]]]}))
                           (when (and (not (session/is-guest?))
                                      (session/dealflow?))
                             (nev-components/association-module-config
                              {:module-label        (loc/tr
                                                     :nev-module/real-estate-target-asset-in-oppos)
                               :module-id           :target-asset-in-oppos
                               :view-id             view-id
                               :nev-entity-type     :entity.type/real-estate
                               :target-entity-type  :entity.type/oppo
                               :associated-entities entities-where-target-asset
                               :save-collapsed-status! (partial events/save-module-collapsed!
                                                                :entity.type/real-estate)
                               :new-entity-start-value {:oppo/target-asset (:db/id real-estate)
                                                        :oppo/name         (:asset/name
                                                                            real-estate)}
                               :add-new-button?     true
                               :add-new-content
                               (when (session/deal-creation-flow?)
                                 [nev-components/opportunity-creator-dropdown
                                  {:entity-type :entity.type/oppo
                                   :initial-target real-estate}])}))

                           (when deals-where-re-target
                             (nev-components/entity-listing-module
                              {:module-id      :target-in-RE-deals
                               :view-id        :fund
                               :module-title   (loc/tr :nev-module/real-estate-target-in-RE-deals)
                               :entity         real-estate
                               :shown-entities deals-where-re-target
                               :shown-columns  (->>
                                                selected-deal-columns
                                                (map (fn [k]
                                                       {:key   k
                                                        :label (let [fs (registry/get-field-schema
                                                                         tenant-schema
                                                                         k)]
                                                                 (loc/field-name k fs))})))}))]
                          (when-not (session/is-guest?)
                            (associated-entities/modules
                             {:entity-id real-estate-id
                              :save-module-collapsed! events/save-module-collapsed!
                              :module-collapsed-path module-collapsed-path}))
                          [;; removing for now:
                           #_(when (plan/available :feature/sell-side-guest
                                                   @session/tenant
                                                   @session/role)
                               {:module-id        :guest-accounts
                                :header           (loc/tr :nev-module/guest-accounts)
                                :collapsed?       (get-in @session/view-config
                                                          (module-collapsed-path :guest-accounts))
                                :save-module-collapsed! (partial events/save-module-collapsed!
                                                                 :entity.type/real-estate)
                                :start-collapsed? (empty? existing-guests)
                                :content          [:div
                                                   [guests-module/view
                                                    {:entity-id          real-estate-id
                                                     :fields             [{:attribute
                                                                           :role/guest-sell-side
                                                                           :label
                                                                           (loc/tr :sell-side-guests)}]
                                                     :show-notification! (partial
                                                                          events/show-notification!
                                                                          notification-atom)
                                                     :show-error!        (partial events/show-error!
                                                                                  error-atom)
                                                     :guest-roles        existing-guests}]]})

                           (when (and (not (session/is-mobile?))
                                      (f-permissions/field-read-access? :common/attachments
                                                                        :entity.type/real-estate
                                                                        nil))
                             (nev-components/attachments-module view-id
                                                                :real-estate-attachments
                                                                real-estate))])}]]]
                     (when-not (session/event-pane-disabled?)
                       [:div
                        {:class "col-span-10 lg:col-span-5"}
                        [event-pane/view
                         {:state       events-pane-atom
                          :entity-ids  [real-estate-id]
                          :module-args {:state-atom state-atom
                                        :view-id    view-id}}]])]]]]])))))})))

(defmethod routing/render-view :entity.type/real-estate
  [{:keys [route-params _view-params]}]
  (let [real-estate-id (-> route-params
                           :id
                           js/parseInt)]
    [default-page-layout/view
     {:class-name "zap-page real-estate"}
     ;;the key change forces rerendering of the component (and its child components)
     ^{:key (str "real-estate-viewer-id-" real-estate-id)}
     [real-estate-view {:real-estate-id real-estate-id}]]))
